import React, { useEffect, useState } from 'react'
import { Keyboard, Modal, Pressable, StyleSheet, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Button, KeyboardAwareView, Text, TextInput } from '@components'
import { useLanguage } from '@context'
import { Theme } from '@theme'

interface ReasonModalProps {
  isVisible: boolean
  title: string
  description: string
  label: string
  confirmLabel: string
  isLoading?: boolean
  onClose: () => void
  onConfirm: (reason: string) => void
}

const MAX_REASON = 500

/** Modal asking for a mandatory free-text reason (the confirm button stays off while empty). */
export const ReasonModal: React.FC<ReasonModalProps> = ({
  isVisible,
  title,
  description,
  label,
  confirmLabel,
  isLoading,
  onClose,
  onConfirm,
}) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)
  const [reason, setReason] = useState('')
  const trimmed = reason.trim()

  const canConfirm = trimmed.length > 0 && !isLoading

  useEffect(() => {
    if (!isVisible) setReason('')
  }, [isVisible])

  const confirm = () => {
    if (canConfirm) onConfirm(trimmed)
  }

  return (
    <Modal visible={isVisible} transparent animationType="fade" onRequestClose={onClose}>
      {/* iOS: the card moves up with the keyboard, so the buttons stay visible. */}
      <KeyboardAwareView isFullScreen>
        {/* A tap outside the field closes the keyboard (not the modal: the reason is kept). */}
        <Pressable style={styles.overlay} onPress={Keyboard.dismiss} accessible={false}>
          <Pressable
            style={styles.card}
            onPress={Keyboard.dismiss}
            accessible={false}
            testID="reason-modal"
          >
            <Text type="subtitleBold">{title}</Text>
            <Text type="bodySmall" color={theme.colors.greyBlue}>
              {description}
            </Text>
            <TextInput
              testID="reason-input"
              title={label}
              value={reason}
              onChangeText={setReason}
              maxLength={MAX_REASON}
              isMultiline
              multiline
              autoFocus
              // The keyboard's ✓ key confirms (a reason is one short paragraph).
              submitBehavior="blurAndSubmit"
              onSubmitEditing={confirm}
            />
            <Text type="bodyExtraSmall" color={theme.colors.greyBlue}>
              {`${reason.length} / ${MAX_REASON}`}
            </Text>
            <View style={styles.actions}>
              <Button title={t('common.cancel')} unfilled onPress={onClose} flex />
              <Button
                testID="reason-confirm"
                title={confirmLabel}
                isRed
                flex
                disabled={!canConfirm}
                onPress={confirm}
              />
            </View>
          </Pressable>
        </Pressable>
      </KeyboardAwareView>
    </Modal>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    overlay: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.45)',
      justifyContent: 'center',
      alignItems: 'center',
      padding: theme.spacing.xl,
    },
    card: {
      width: '100%',
      maxWidth: 440,
      backgroundColor: theme.colors.white,
      borderRadius: theme.border.large,
      padding: theme.spacing.xl,
      gap: theme.spacing.md,
    },
    actions: { flexDirection: 'row', gap: theme.spacing.md, marginTop: theme.spacing.sm },
  })
